import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { fadeInUp } from "../lib/brand";
import { SERVICES, getService } from "../data/services";
import { FRAMEWORK } from "../data/framework";
import { Eyebrow, GoldRule, SectionHeading, PrimaryLink } from "../components/site/Primitives";
import { CTASection } from "../components/site/Sections";
import { FrameworkMountain } from "../components/site/FrameworkGraphic";
import { DimensionsGraphic } from "../components/site/DimensionsGraphic";
import { RidgeLines } from "../components/site/Marks";

const ai = getService("ai-strategy-adoption");
const stagger = (i) => ({ ...fadeInUp, transition: { ...fadeInUp.transition, delay: 0.07 * i } });

const PROGRESSION = ["AI Interest", "AI Strategy", "Organizational Readiness", "Adoption", "Enterprise Value"];

const QUESTIONS = [
  "Where should we start?",
  "Which AI opportunities actually matter?",
  "How do we govern this?",
  "How do we move beyond pilots?",
  "How do we prepare our workforce?",
  "How do we get leaders aligned?",
  "How do we manage risk without stopping innovation?",
  "How do we get employees to actually adopt AI tools?",
  "How should AI capability operate across the organization?",
];

const GAP_DIMENSIONS = [
  { name: "Strategy", description: "Clear priorities and outcomes that connect AI to the mission and operating model." },
  { name: "Governance", description: "Decision rights, accountability, and responsible-use principles that enable innovation while managing risk." },
  { name: "Leadership", description: "Executives and managers prepared to decide, sponsor, and lead AI-enabled work." },
  { name: "Workforce", description: "Literacy, confidence, and practical capability to use AI responsibly." },
  { name: "Adoption", description: "Engagement, learning, and reinforcement that turn pilots into everyday practice." },
];

const AI_DISCIPLINES = [
  "Establish what AI adoption means for your organization, why it matters, and what success looks like.",
  "Understand leadership, workforce, governance, and cultural readiness before scaling investment.",
  "Secure executive sponsorship and shared decisions about priorities, risk, and pace.",
  "Engage the people whose daily work will change—so adoption is owned, not imposed.",
  "Reinforce, measure, and build the organizational capability to keep adapting as AI evolves.",
];

const Hero = () => (
  <section data-testid="service-hero" className="relative pt-40 pb-16 md:pt-48 md:pb-20 bg-summit-bg overflow-hidden">
    <RidgeLines className="absolute inset-x-0 bottom-0 h-64 md:h-80 w-full opacity-60 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10">
      <motion.div {...fadeInUp} className="max-w-4xl">
        <Eyebrow className="mb-6">Lead Offering · AI Strategy & Adoption Consulting</Eyebrow>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-summit-charcoal">
          Moving AI From Experimentation to <span className="italic text-summit-gold-dark">Enterprise Impact</span>
        </h1>
        <GoldRule className="my-8" />
        <p className="font-body text-base md:text-lg text-summit-charcoal leading-relaxed max-w-2xl">{ai.heroCopy}</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <PrimaryLink to="/contact" testId="service-hero-cta">Schedule an AI Strategy Conversation</PrimaryLink>
          <a
            href="#how-summit-helps"
            data-testid="ai-hero-secondary-cta"
            onClick={(e) => { e.preventDefault(); document.getElementById("how-summit-helps")?.scrollIntoView({ behavior: "smooth" }); }}
            className="group inline-flex items-center gap-3 font-body text-sm font-medium tracking-wide px-7 py-4 border border-summit-charcoal/25 text-summit-charcoal hover:border-summit-gold hover:text-summit-gold-dark transition-colors"
          >
            See How Summit Helps <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const Progression = () => (
  <section data-testid="ai-progression" className="py-16 md:py-20 bg-summit-bg border-y border-summit-border">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <motion.p {...fadeInUp} className="font-body text-xs uppercase tracking-[0.22em] text-summit-gold-dark font-semibold mb-10">The Path Summit Helps You Lead</motion.p>
      <ol className="relative grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: "easeInOut" }}
          className="hidden md:block absolute top-[7px] left-[10%] right-[10%] h-px bg-summit-gold origin-left"
        />
        {PROGRESSION.map((step, i) => (
          <motion.li key={step} {...stagger(i * 2)} data-testid={`ai-progression-step-${i}`} className="relative flex md:flex-col md:items-center gap-4 md:gap-5 md:text-center">
            <span className={`relative z-10 mt-0.5 md:mt-0 w-4 h-4 rotate-45 shrink-0 border ${i === PROGRESSION.length - 1 ? "bg-summit-forest border-summit-forest" : "bg-summit-bg border-summit-gold"}`} />
            <span>
              <span className="block font-body text-[10px] uppercase tracking-[0.2em] text-summit-text-muted">Stage {i + 1}</span>
              <span className={`block font-heading text-2xl leading-tight ${i === PROGRESSION.length - 1 ? "text-summit-forest font-medium" : "text-summit-charcoal"}`}>{step}</span>
            </span>
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

const Questions = () => (
  <section data-testid="ai-questions" className="py-24 md:py-32 bg-summit-bg">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <SectionHeading eyebrow="For Executive Teams" title="You know AI matters. The harder questions come next." copy="Summit works with presidents, chief executives, provosts, and senior leadership teams who are answering these questions now." />
      </div>
      <ul className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-summit-border">
        {QUESTIONS.map((q, i) => (
          <motion.li key={q} {...stagger(i)} data-testid={`ai-question-${i}`} className="border-b border-r border-summit-border p-6 flex items-start gap-3 hover:bg-summit-sand transition-colors">
            <span className="mt-2.5 w-1.5 h-1.5 rotate-45 bg-summit-gold shrink-0" />
            <span className="font-heading text-xl text-summit-charcoal leading-snug">{q}</span>
          </motion.li>
        ))}
      </ul>
    </div>
  </section>
);

const Gap = () => (
  <section id="adoption-gap" data-testid="ai-gap" className="py-24 md:py-32 bg-summit-sand">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-6">
          <SectionHeading eyebrow="The AI Adoption Gap" title="Powerful tools are widely available. Organizational readiness is not." />
        </div>
        <motion.div {...stagger(2)} className="lg:col-span-5 lg:col-start-8 lg:pt-14">
          <p className="font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">
            Most organizations now have access to capable AI. Far fewer have established the conditions to use it effectively at scale.
          </p>
          <p className="mt-4 font-body text-base text-summit-text-muted leading-relaxed">
            The gap is organizational, not technical—and it shows up in five dimensions.
          </p>
        </motion.div>
      </div>
      <DimensionsGraphic dimensions={GAP_DIMENSIONS} centerLabel={["SUSTAINABLE", "AI Value"]} />
    </div>
  </section>
);

const Help = () => (
  <section id="how-summit-helps" data-testid="service-offerings" className="py-24 md:py-32 bg-summit-bg scroll-mt-24">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
        <SectionHeading eyebrow="How Summit Helps" title="Eight ways we help organizations adopt AI well." />
        <motion.p {...stagger(2)} className="font-body text-base text-summit-text-muted leading-relaxed max-w-md">
          Engagements are scoped to your starting point—from an initial strategy and readiness assessment to enterprise-wide adoption.
        </motion.p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-summit-border">
        {ai.offerings.map((o, i) => (
          <motion.div key={o.title} {...stagger(i)} data-testid={`offering-${i}`} className="group border-b border-r border-summit-border p-7 lg:p-8 flex flex-col hover:bg-summit-sand transition-colors">
            <span className="font-heading text-3xl text-summit-gold-dark leading-none">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-5 font-heading text-2xl font-medium text-summit-charcoal leading-tight">{o.title}</h3>
            <p className="mt-3 font-body text-sm text-summit-text-muted leading-relaxed flex-1">{o.description}</p>
            <p className="mt-5 pt-4 border-t border-summit-border font-body text-xs text-summit-gold-dark italic">Answers: “{o.answers}”</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const FrameworkTie = () => {
  const [active, setActive] = useState(0);
  return (
    <section data-testid="ai-framework" className="py-24 md:py-32 bg-white border-y border-summit-border">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Proprietary Methodology" title="AI adoption follows the same five disciplines." />
          </div>
          <motion.p {...stagger(2)} className="lg:col-span-5 lg:pt-14 font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">
            Summit applies the {FRAMEWORK.name} to AI adoption—because AI succeeds or stalls for the same organizational reasons every transformation does.
          </motion.p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <FrameworkMountain active={active} onActive={setActive} showLabels={false} idPrefix="ai-framework-node" />
          </div>
          <ol className="lg:col-span-6 divide-y divide-summit-border">
            {FRAMEWORK.phases.map((p, i) => (
              <li
                key={p.n}
                data-testid={`ai-discipline-${p.n}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
                className={`py-5 grid grid-cols-[48px_1fr] gap-4 cursor-pointer transition-colors ${active === i ? "text-summit-gold-dark" : ""}`}
              >
                <span className="font-heading text-3xl leading-none text-summit-gold-dark">{p.n}</span>
                <span>
                  <span className={`block font-heading text-2xl font-medium leading-tight ${active === i ? "text-summit-gold-dark" : "text-summit-charcoal"}`}>{p.title}</span>
                  <span className="block mt-1.5 font-body text-sm text-summit-text-muted leading-relaxed">{AI_DISCIPLINES[i]}</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
        <motion.div {...stagger(1)} className="mt-12">
          <Link to="/services/organizational-transformation" data-testid="ai-framework-link" className="group inline-flex items-center gap-2 font-body text-sm font-semibold text-summit-forest hover:text-summit-gold-dark transition-colors">
            <span className="link-underline">Explore the {FRAMEWORK.name}</span>
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const Challenge = () => (
  <section data-testid="ai-transformation-challenge" className="relative py-24 md:py-32 bg-summit-forest text-white overflow-hidden grain">
    <RidgeLines className="absolute inset-x-0 top-0 h-full w-full opacity-30 pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <SectionHeading light eyebrow="AI Is a Transformation Challenge" title="Technology is the entry point. Adaptation is the advantage." />
      </div>
      <motion.blockquote {...stagger(2)} className="lg:col-span-8 lg:pl-10 lg:border-l border-summit-gold/40">
        <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] text-white">
          “The organizations that gain the greatest value from AI will not simply be the organizations with the best technology. They will be the organizations that <span className="text-summit-gold-light italic">learn how to adapt the fastest</span>.”
        </p>
        <p className="mt-8 font-body text-sm text-white/70 leading-relaxed max-w-xl">
          That is why Summit approaches AI as organizational transformation: strategy, governance, leadership, workforce capability, and adoption—moving together.
        </p>
      </motion.blockquote>
    </div>
  </section>
);

const Related = () => (
  <section data-testid="related-services" className="py-20 bg-summit-bg border-t border-summit-border">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <p className="font-body text-xs uppercase tracking-[0.22em] text-summit-gold-dark font-semibold mb-8">Related Services</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SERVICES.filter((s) => s.slug !== ai.slug).map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-${s.slug}`} className="group border border-summit-border p-6 card-lift bg-white">
            <h3 className="font-heading text-2xl text-summit-charcoal group-hover:text-summit-gold-dark transition-colors">{s.title}</h3>
            <p className="mt-2 font-body text-sm text-summit-text-muted">{s.summary}</p>
            <span className="mt-4 inline-flex items-center gap-2 font-body text-sm font-semibold text-summit-forest">Explore {s.shortTitle} <ArrowRight size={14} /></span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function AIStrategy() {
  useSEO(ai.seoTitle, ai.seoDescription);
  return (
    <>
      <Hero />
      <Progression />
      <Questions />
      <Gap />
      <Help />
      <FrameworkTie />
      <Challenge />
      <CTASection
        eyebrow="A Practical Path"
        heading="Your organization does not need another AI presentation."
        copy="It needs a practical path from possibility to adoption."
        primaryLabel="Schedule an AI Strategy Conversation"
        testId="service-cta"
      />
      <Related />
    </>
  );
}
